import React from "react";
import { Helmet } from "react-helmet-async";
import Grid, { type Actions, type Columns } from "../../component/Grid";
import { useCrumbs } from "../../component/breadcrumbs";
import { useNavigate, useSearchParams } from "react-router-dom";
import useLocale from "../../util/i18n";
import { dismissSalesShipment, getSalesShipment, getShipmentStatuses, saveSalesShipment, type SalesShipment, type SalesShipmentItem } from "../../api/erp";
import { getConfig } from "../../api/core";
import { type OptionType } from "../../component/select";
import { MDBBtn, MDBInput, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter } from "mdb-react-ui-kit";
import ExcelJS from "exceljs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Toast from "../../component/toast";

export default function SalesShipments() {
    let { 0: editable, 1: setEditable } = React.useState(false);
    let { 0: query, 1: setQuery } = useSearchParams();
    let { 0: statuses, 1: setStatuses } = React.useState<Array<OptionType>>([]);
    let { 0: setCrumbs } = useCrumbs();
    let { 0: __ } = useLocale();
    let { 0: loading, 1: setLoading } = React.useState(false);
    let navigate = useNavigate();
    let columns = React.useMemo<Columns>(() => ({
        shipment_id: {
            label: 'ID',
            filterable: 'filter[shipment_id]'
        },
        order_id: {
            label: __('Order ID')
        },
        customer_id: {
            label: __('Customer ID')
        },
        customer_name: {
            label: __('Customer Name')
        },
        status: {
            label: __('Status'),
            className: 'w-1 text-center text-nowrap',
            filterable: 'filter[status]',
            type: 'select',
            options: statuses
        }
    }), [__, statuses]);
    let actions = React.useMemo<Actions>(() => ([
        {
            label: __(editable ? 'Edit' : 'View'),
            onClick: (item: SalesShipment) => {
                window.sessionStorage.setItem('SS-' + item.id, JSON.stringify(item));
                navigate('/sales-shipment/' + (editable ? 'edit' : 'view') + '?id=' + item.id);
            }
        },
        {
            label: __('Excluded in Statistics'),
            condition: item => item.dismiss != 1,
            onClick: (item, ref) => {
                dismissSalesShipment({ id: item.id, dismiss: 1 }).then(() => {
                    ref.redraw({ ...item, dismiss: 1 });
                });
            }
        },
        {
            label: __('Included in Statistics'),
            condition: item => item.dismiss == 1,
            onClick: (item, ref) => {
                dismissSalesShipment({ id: item.id, dismiss: 0 }).then(() => {
                    ref.redraw({ ...item, dismiss: 0 });
                });
            }
        }
    ]), [__, editable]);
    const doLoad = React.useCallback((params: Record<string, any>) => {
        if (!params.sort) {
            params.sort = 'shipment_id DESC';
        }
        return getSalesShipment(params);
    }, []);
    const upload = React.useRef<(file: File) => Promise<boolean>>(null);
    let onDropFile = React.useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let files = e.dataTransfer.files;
        if (files.length === 0) {
            return;
        }
        setLoading(true);
        for (let i = 0; i < files.length; i++) {
            const file = files.item(i);
            file && (await upload.current?.(file));
        }
        setLoading(false);
        document.location.reload();
    }, [setLoading]);
    React.useEffect(() => {
        setCrumbs(['Shipment']);
        if (window.sessionStorage.getItem('erp-editable')) {
            setEditable(window.sessionStorage.getItem('erp-editable') === '1');
        } else {
            getConfig('erp/service').then(response => {
                let result = !Boolean(response['erp/service']) ? '1' : '0';
                window.sessionStorage.setItem('erp-editable', result);
                setEditable(result === '1');
            });
        }
        let statuses = window.sessionStorage.getItem('SS-status');
        if (statuses) {
            setStatuses(JSON.parse(statuses));
        } else {
            getShipmentStatuses().then(response => {
                let result = response.map(item => ({ value: item.status, label: item.status }));
                window.sessionStorage.setItem('SS-status', JSON.stringify(result));
                setStatuses(result);
            });
        }
    }, [setEditable, setStatuses]);
    return (
        <>
            <Helmet>
                <title>{__('Shipment')}</title>
            </Helmet>
            <Grid newUrl={editable ? '/sales-shipment/edit' : undefined} onDrop={editable ? onDropFile : undefined} columns={columns} actions={actions} query={query} setQuery={setQuery} doLoad={doLoad} />
            {editable ? (
                <>
                    <Import ref={upload} />
                    {loading ? (
                        <div className="loading-modal">
                            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                        </div>
                    ) : null}
                </>
            ) : null}
        </>
    );
}

const Import = React.forwardRef<(file: File) => Promise<boolean>>(function (_, ref) {
    let { 0: show, 1: setShow } = React.useState(false);
    let { 0: loading, 1: setLoading } = React.useState(false);
    let { 0: __ } = useLocale();
    let template = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet();
        sheet.getCell(1, 1).value = __('Shipment ID');
        sheet.getCell(2, 1).value = __('Order ID');
        sheet.getCell(3, 1).value = __('Customer ID');
        sheet.getCell(4, 1).value = __('Customer Name');
        sheet.getCell(5, 1).value = __('Status');
        sheet.getCell(6, 1).value = 'SKU';
        sheet.getCell(6, 2).value = __('Qty');
        sheet.getCell(1, 1).style.font = sheet.getCell(2, 1).style.font = sheet.getCell(3, 1).style.font = sheet.getCell(4, 1).style.font = sheet.getCell(5, 1).style.font =
            sheet.getCell(6, 1).style.font = sheet.getCell(6, 2).style.font = { bold: true };
        workbook.xlsx.writeBuffer().then(buffer => {
            let a = document.createElement('a');
            a.download = 'template-SS.xlsx';
            a.href = URL.createObjectURL(new Blob([buffer]));
            document.body.append(a);
            a.click();
            URL.revokeObjectURL(a.href);
            a.remove();
        });
    }, [__]);
    let doSubmit = React.useCallback(async (file: File) => {
        try {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(await file.arrayBuffer());
            let params: Array<Partial<SalesShipment> & { item: Array<SalesShipmentItem> }> = [];
            workbook.eachSheet(sheet => {
                let order: Partial<SalesShipment> & { item: Array<SalesShipmentItem> } = {
                    shipment_id: sheet.getCell(1, 2).text,
                    order_id: sheet.getCell(2, 2).text,
                    customer_id: sheet.getCell(3, 2).text,
                    customer_name: sheet.getCell(4, 2).text,
                    status: sheet.getCell(5, 2).text,
                    item: []
                };
                if (!order.shipment_id) {
                    return;
                }
                for (let r = 7; ; r++) {
                    let row = sheet.getRow(r);
                    let item: SalesShipmentItem = {
                        id: '',
                        shipment_id: order.shipment_id,
                        sku: row.getCell(1).text.trim(),
                        qty: parseFloat(row.getCell(2).text) || 0
                    }
                    if (!item.sku) {
                        break;
                    }
                    if (item.qty > 0) {
                        order.item.push(item);
                    }
                }
                params.push(order);
            });
            await saveSalesShipment(params);
            return true;
        } catch {
            return false;
        }
    }, [])
    let onSubmit = React.useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let file = (new FormData(e.target as HTMLFormElement)).get('file') as File | null;
        if (file) {
            setLoading(true);
            if (await doSubmit(file)) {
                setShow(false);
                Toast.show(__('Data has been saved.')).then(() => {
                    window.location.reload();
                });
            }
            setLoading(false);
        }
    }, [setLoading, setShow]);
    React.useEffect(() => {
        let l = setShow.bind(null, true);
        document.addEventListener('import', l);
        return () => {
            document.removeEventListener('import', l);
        };
    }, [setShow]);
    React.useImperativeHandle(ref, () => doSubmit, [doSubmit]);
    return (
        <>
            <div data-importable="1" />
            <MDBModal open={show} onClose={setShow.bind(null, false)} tabIndex='-1'>
                <MDBModalDialog centered>
                    <MDBModalContent tag="form" onSubmit={onSubmit}>
                        <MDBModalBody>
                            {loading ? (
                                <div className="d-flex justify-content-center">
                                    <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                                </div>
                            ) : (
                                <MDBInput type="file" name="file" label={__('File')} className="active" required accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
                            )}
                        </MDBModalBody>
                        <MDBModalFooter>
                            <MDBBtn type="button" color="info" onClick={template}>{__('Template')}</MDBBtn>
                            <MDBBtn disabled={loading} type="button" color="secondary" onClick={setShow.bind(null, false)}>{__('Cancel')}</MDBBtn>
                            <MDBBtn disabled={loading} type="submit">{__('Import')}</MDBBtn>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </>
    );
});
